// Product-card size dropdown accessibility.
//
// The card's size "select" is a custom listbox: a <button class="product-item__option">
// trigger followed by a <ul class="product-item__dropdown"> of <li> options. The
// card controllers open/close it by toggling `.active` on the <ul> (the dropdown,
// NOT the trigger) and select a value on <li> click. That is mouse-only and
// silent to screen readers.
//
// This module layers the ARIA listbox keyboard interaction on top, via event
// delegation on `document` so it covers server- and JS-rendered cards without
// touching each controller. It toggles the SAME `.active` class on the <ul> the
// controllers use, and selects by dispatching a click on the option (so the
// existing selection logic still runs).

const TRIGGER = '.product-item__option';
const LIST = '.product-item__dropdown';
const OPTION = '.product-item__dropdown__item';

const isActivateKey = (e) =>
  e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';

const listOf = (trigger) => {
  const next = trigger.nextElementSibling;
  return next && next.matches(LIST)
    ? next
    : trigger.parentElement?.querySelector(LIST) || null;
};

const triggerOf = (list) => {
  const prev = list.previousElementSibling;
  return prev && prev.matches(TRIGGER)
    ? prev
    : list.parentElement?.querySelector(TRIGGER) || null;
};

// Open state lives on the <ul>, not the trigger.
const isOpen = (list) => !!list && list.classList.contains('active');

const enabledOptions = (list) =>
  [...list.querySelectorAll(OPTION)].filter(
    (li) =>
      !li.classList.contains('disabled') &&
      li.getAttribute('aria-disabled') !== 'true'
  );

const backdropFor = (trigger) => {
  const card =
    trigger.closest('.product-item__body') || trigger.closest('.product-item');
  return card ? card.querySelector('.product-item__backdrop') : null;
};

// A visibility:hidden / mid-transition element can't be focused, so a single
// focus() right after opening can no-op. Retry across frames until it takes.
const focusOptionWhenReady = (option, attempts = 12) => {
  if (!option) return;
  option.focus();
  if (document.activeElement === option || attempts <= 0) return;
  requestAnimationFrame(() => focusOptionWhenReady(option, attempts - 1));
};

// Move focus to the currently selected option, else the first (or last) one.
const focusInitialOption = (list, focusLast) => {
  if (!list) return;
  const opts = enabledOptions(list);
  if (!opts.length) return;
  const selected = list.querySelector(`${OPTION}.active`);
  const target =
    selected && opts.indexOf(selected) > -1
      ? selected
      : focusLast
      ? opts[opts.length - 1]
      : opts[0];
  focusOptionWhenReady(target);
};

const openDropdown = (trigger, list, focusLast) => {
  if (!list) return;
  list.classList.add('active');
  trigger.setAttribute('aria-expanded', 'true');
  backdropFor(trigger)?.classList.add('active');
  focusInitialOption(list, focusLast);
};

const closeDropdown = (trigger, list, returnFocus) => {
  if (list) list.classList.remove('active');
  trigger?.setAttribute('aria-expanded', 'false');
  backdropFor(trigger)?.classList.remove('active');
  if (returnFocus) trigger?.focus();
};

const onOptionKeydown = (e, option) => {
  const list = option.closest(LIST);
  if (!list) return;
  const trigger = triggerOf(list);
  const opts = enabledOptions(list);
  const i = opts.indexOf(option);

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    opts[Math.min(i + 1, opts.length - 1)]?.focus();
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    opts[Math.max(i - 1, 0)]?.focus();
  } else if (e.key === 'Home') {
    e.preventDefault();
    opts[0]?.focus();
  } else if (e.key === 'End') {
    e.preventDefault();
    opts[opts.length - 1]?.focus();
  } else if (isActivateKey(e)) {
    e.preventDefault();
    opts.forEach((o) => o.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
    option.click(); // let the card controller select the value
    closeDropdown(trigger, list, true);
  } else if (e.key === 'Escape') {
    e.preventDefault();
    closeDropdown(trigger, list, true);
  } else if (e.key === 'Tab') {
    closeDropdown(trigger, list, false);
  }
};

const onTriggerKeydown = (e, trigger) => {
  if (trigger.disabled) return;
  const list = listOf(trigger);
  const open = isOpen(list);

  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    if (open) {
      const opts = enabledOptions(list);
      (e.key === 'ArrowDown' ? opts[0] : opts[opts.length - 1])?.focus();
    } else {
      openDropdown(trigger, list, e.key === 'ArrowUp');
    }
  } else if (e.key === 'Escape' && open) {
    e.preventDefault();
    closeDropdown(trigger, list, true);
  }
  // Enter/Space fall through: the trigger is a real <button>, so they fire a
  // native click that the card controller toggles the dropdown with (same path
  // as a mouse click). The click listener below then moves focus in on open.
};

const init = () => {
  document.addEventListener('keydown', (e) => {
    const option = e.target.closest(OPTION);
    if (option) {
      onOptionKeydown(e, option);
      return;
    }
    const trigger = e.target.closest(TRIGGER);
    if (trigger) onTriggerKeydown(e, trigger);
  });

  document.addEventListener('click', (e) => {
    const option = e.target.closest(OPTION);
    if (option) {
      const list = option.closest(LIST);
      list
        ?.querySelectorAll(OPTION)
        .forEach((o) => o.setAttribute('aria-selected', 'false'));
      option.setAttribute('aria-selected', 'true');
      return;
    }
    const trigger = e.target.closest(TRIGGER);
    if (!trigger) return;
    // Mouse click AND keyboard Enter/Space both arrive here via the button's
    // native click, which the card controller uses to toggle `.active` on the
    // <ul>. After that handler runs, sync aria-expanded and move focus into the
    // list when it opened. (:focus-visible keeps mouse users ring-free.)
    const list = listOf(trigger);
    requestAnimationFrame(() => {
      const open = isOpen(list);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) focusInitialOption(list, false);
    });
  });
};

export default init;
